import { zodResolver } from '@hookform/resolvers/zod';
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Plug, RefreshCw, Unplug, XCircle } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';
import { QueryKey } from '@/Config/api/queryKey';
import { IntegrationEnabledBadge } from '@/Domain/components/Integration/IntegrationEnabledBadge';
import { IntegrationStatusBadge } from '@/Domain/components/Integration/IntegrationStatusBadge';
import { IntegrationConnectionCard } from '@/Domain/components/Integration/components/IntegrationConnectionCard';
import { IntegrationControlField } from '@/Domain/components/Integration/components/IntegrationControlField';
import { IntegrationIntroduction } from '@/Domain/components/Integration/components/IntegrationIntroduction';
import { IntegrationSection } from '@/Domain/components/Integration/components/IntegrationSection';
import { IntegrationToggleField } from '@/Domain/components/Integration/components/IntegrationToggleField';
import { Button, IconButton } from '@/Domain/components/Shared/Button/Button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/Domain/components/Shared/Select/Select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/Domain/components/Shared/Sheet/sheet';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import CloseIcon from '@/Domain/components/icons/Close.icon';
import { IntercomIcon } from '@/Domain/components/icons/Intercom.icon';
import { useIntegrationConnectFunnel } from '@/Domain/hooks/Integration/useIntegrationConnectFunnel.hook';
import { SCENARIO_ID } from '@/context';

const INTERCOM_NS_PREFIX = 'integration:intercom';

// Public Intercom OAuth app identifier (mirrors settings.INTERCOM_CLIENT_ID in Django).
// Not a secret — the client_secret is held server-side and never exposed to the browser.
const INTERCOM_CLIENT_ID = 'c4be8e81-aaa7-4d60-ba62-8545df9748a3';

const INTERCOM_INTEGRATION_NAME = 'intercom' as const;
const INTERCOM_QUERY_KEY = [QueryKey.integration.get, INTERCOM_INTEGRATION_NAME] as const;
const AUTO_CLOSE_QUERY_KEY = [QueryKey.integration.autoCloseMinutes] as const;

const AUTO_CLOSE_VALUES = [null, 5, 10, 30] as const;
type AutoCloseValue = (typeof AUTO_CLOSE_VALUES)[number];

// "null" isn't a valid select value in Radix; use this sentinel to represent it.
const AUTO_CLOSE_NEVER_SENTINEL = '__never__';

// Fields on the `params` object must match IntercomIntegrationParams in
// backend/appquickchat/appquickchat/api/integrations/integration_params_schemas.py.
interface IntercomIntegrationParams {
  intercom_bot_active: boolean;
  intercom_verify_token: string | null;
  intercom_access_token: string | null;
  intercom_app_id: string | null;
  intercom_admin_id: string | null;
  intercom_admin_name: string | null;
  intercom_handoff: boolean;
  intercom_only_admin: boolean;
}

// The wire shape from GET /v1/api/integration/intercom is the standard
// IntegrationData wrapper (see appquickchat/api/integrations/schemas.py).
interface IntercomIntegrationResponse {
  name: typeof INTERCOM_INTEGRATION_NAME;
  params: IntercomIntegrationParams;
}

interface AutoCloseResponse {
  auto_close_minutes: AutoCloseValue;
  valid_values: AutoCloseValue[];
}

interface ValidateTokenResponse {
  status: 'ok' | 'error';
  admin_name?: string;
  message?: string;
}

type IntercomFormValues = {
  intercom_handoff: boolean;
  // UI field: when true (on), `intercom_only_admin` is sent as false.
  reply_to_all: boolean;
  auto_close: string;
};

const formSchema = z.object({
  intercom_handoff: z.boolean(),
  reply_to_all: z.boolean(),
  auto_close: z.string(),
});

const autoCloseOptionKey = (value: AutoCloseValue): string =>
  value === null ? AUTO_CLOSE_NEVER_SENTINEL : String(value);

const parseAutoCloseKey = (key: string): AutoCloseValue => {
  if (key === AUTO_CLOSE_NEVER_SENTINEL) return null;
  const num = Number(key);
  return (AUTO_CLOSE_VALUES as readonly AutoCloseValue[]).includes(num as AutoCloseValue)
    ? (num as AutoCloseValue)
    : null;
};

const buildOAuthUrl = (clientId: string, verifyToken: string): string => {
  const state = encodeURIComponent(
    JSON.stringify({ scenario_id: SCENARIO_ID, verify_token: verifyToken }),
  );
  return `https://app.intercom.com/oauth?client_id=${clientId}&state=${state}`;
};

interface IntercomSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const IntercomSheet: React.FC<IntercomSheetProps> = ({ open, onOpenChange }) => {
  const { t } = useTranslation(['integration']);
  const queryClient = useQueryClient();
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);

  const integrationQuery = useQuery<IntercomIntegrationResponse>({
    queryKey: INTERCOM_QUERY_KEY,
    enabled: open,
  });

  const autoCloseQuery = useQuery<AutoCloseResponse>({
    queryKey: AUTO_CLOSE_QUERY_KEY,
    enabled: open,
  });

  const integrationData = integrationQuery.data?.params;
  const isConnected = Boolean(integrationData?.intercom_access_token);

  const { trackConnectStarted, trackConnectFailed } = useIntegrationConnectFunnel(
    'intercom',
    isConnected,
  );

  const patchIntegrationMutation = useMutation<
    unknown,
    Error,
    {
      name: typeof INTERCOM_INTEGRATION_NAME;
      params: {
        intercom_handoff?: boolean;
        intercom_only_admin?: boolean;
      };
    }
  >({
    mutationKey: [QueryKey.integration.patch],
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INTERCOM_QUERY_KEY });
      toast.success(t(`${INTERCOM_NS_PREFIX}.saved`));
    },
    onError: () => {
      toast.error(t(`${INTERCOM_NS_PREFIX}.save_failed`));
    },
  });

  // Instant enable/disable toggle, decoupled from the settings Save: standardized
  // "<name> integration enabled/disabled" toast and never closes the sheet.
  const toggleActiveMutation = useMutation<
    unknown,
    Error,
    { name: typeof INTERCOM_INTEGRATION_NAME; params: { intercom_bot_active: boolean } }
  >({
    mutationKey: [QueryKey.integration.patch],
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: INTERCOM_QUERY_KEY });
      const name = t(`${INTERCOM_NS_PREFIX}.title`);
      toast.success(
        variables.params.intercom_bot_active
          ? t('integrations:toast.enabled', { name })
          : t('integrations:toast.disabled', { name }),
      );
    },
    onError: () => {
      toast.error(t(`${INTERCOM_NS_PREFIX}.save_failed`));
    },
  });

  const handleToggleActive = (checked: boolean) => {
    toggleActiveMutation.mutate({
      name: INTERCOM_INTEGRATION_NAME,
      params: { intercom_bot_active: checked },
    });
  };

  const patchAutoCloseMutation = useMutation<unknown, Error, AutoCloseValue>({
    mutationKey: [QueryKey.integration.autoCloseMinutesPatch],
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AUTO_CLOSE_QUERY_KEY });
    },
    onError: () => {
      toast.error(t(`${INTERCOM_NS_PREFIX}.save_failed`));
    },
  });

  const validateTokenMutation = useMutation<ValidateTokenResponse>({
    mutationKey: [QueryKey.integration.intercomValidateToken],
    onSuccess: (data) => {
      if (data.status === 'ok') {
        toast.success(t(`${INTERCOM_NS_PREFIX}.test_succeeded`, { name: data.admin_name || '' }));
      } else {
        toast.error(t(`${INTERCOM_NS_PREFIX}.test_failed`, { message: data.message || '' }));
      }
    },
    onError: () => {
      toast.error(t(`${INTERCOM_NS_PREFIX}.test_failed`, { message: '' }));
    },
  });

  const removeMutation = useMutation({
    mutationKey: [QueryKey.integration.intercomRemove],
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INTERCOM_QUERY_KEY });
      setConfirmRemoveOpen(false);
    },
    onError: () => {
      toast.error(t(`${INTERCOM_NS_PREFIX}.remove_failed`));
    },
  });

  const defaultValues: IntercomFormValues = useMemo(
    () => ({
      intercom_handoff: integrationData?.intercom_handoff ?? false,
      // Invert for UI: "Reply to all" on == intercom_only_admin false.
      reply_to_all: integrationData ? !integrationData.intercom_only_admin : true,
      auto_close: autoCloseOptionKey(autoCloseQuery.data?.auto_close_minutes ?? null),
    }),
    [integrationData, autoCloseQuery.data],
  );

  const { control, reset } = useForm<IntercomFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  // Reset form whenever the server-returned values change (after fetch or invalidation).
  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const handleAuthenticate = useCallback(() => {
    // Only count a fresh connection: this handler is shared with the reconnect
    // button, and a reconnect (already connected) can't produce the hook's
    // false->true success transition, so it would be an unterminated start.
    if (!isConnected) trackConnectStarted();
    const verifyToken = integrationData?.intercom_verify_token;
    if (!verifyToken) {
      trackConnectFailed('missing_verify_token');
      toast.error(t(`${INTERCOM_NS_PREFIX}.oauth_failed`));
      return;
    }
    // Full-page redirect to Intercom; the callback returns the browser to the
    // Channels page with a status the app toasts and reopens this sheet on.
    window.location.href = buildOAuthUrl(INTERCOM_CLIENT_ID, verifyToken);
  }, [integrationData, isConnected, t, trackConnectStarted, trackConnectFailed]);

  const handleTestConnection = useCallback(() => {
    validateTokenMutation.mutate();
  }, [validateTokenMutation]);

  const handleRemove = useCallback(() => {
    removeMutation.mutate();
  }, [removeMutation]);

  const isLoading = integrationQuery.isLoading || autoCloseQuery.isLoading;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className="max-w-xl w-full h-full pb-0"
        tabIndex={undefined}
        data-testid="intercom-sheet"
        onEscapeKeyDown={(e) => {
          e.preventDefault();
          onOpenChange(false);
        }}
      >
        <SheetHeader className="px-0.5 items-center flex-row">
          <div className="flex items-center gap-3">
            <IntercomIcon className="size-8 shrink-0" />
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <SheetTitle data-testid="intercom-sheet-title">
                  {t(`${INTERCOM_NS_PREFIX}.title`)}
                </SheetTitle>
                <IntegrationStatusBadge connected={isConnected} />
                <IntegrationEnabledBadge enabled={Boolean(integrationData?.intercom_bot_active)} />
              </div>
            </div>
          </div>
          <IconButton
            onClick={() => onOpenChange(false)}
            variant="secondary"
            data-testid="intercom-close-button"
            className="shrink-0"
          >
            <CloseIcon />
          </IconButton>
        </SheetHeader>

        <div className="overflow-y-auto px-0.5 pb-6 space-y-6 flex-1">
          {isLoading ? (
            <div className="flex items-center justify-center py-12" data-testid="intercom-loading">
              <Spinner className="size-6 text-foreground-subtle" />
            </div>
          ) : (
            <>
              <IntegrationIntroduction
                description={t(`${INTERCOM_NS_PREFIX}.overview`)}
                prerequisites={
                  t(`${INTERCOM_NS_PREFIX}.prerequisites`, { returnObjects: true }) as string[]
                }
                docsHref="https://docs.quickchat.ai/channels/intercom"
              />

              <IntegrationSection
                title={t('integrations:section.connect')}
                data-testid="intercom-connect-enable-section"
              >
                {!isConnected && (
                  <Button
                    type="button"
                    variant="dark"
                    size="sm"
                    className="w-full"
                    onClick={handleAuthenticate}
                    data-testid="intercom-authenticate-button"
                  >
                    <Plug className="size-4" />
                    {t('integrations:connect', { name: 'Intercom' })}
                  </Button>
                )}

                {isConnected && (
                  <IntegrationConnectionCard
                    data-testid="intercom-connection-card"
                    details={[
                      {
                        label: t(`${INTERCOM_NS_PREFIX}.connected_as`),
                        value: integrationData?.intercom_admin_name || '',
                        'data-testid': 'intercom-connected-as',
                      },
                      {
                        label: t(`${INTERCOM_NS_PREFIX}.app_id`),
                        value: integrationData?.intercom_app_id || '',
                        'data-testid': 'intercom-app-id',
                      },
                      {
                        label: t(`${INTERCOM_NS_PREFIX}.admin_id`),
                        value: integrationData?.intercom_admin_id || '',
                        'data-testid': 'intercom-admin-id',
                      },
                    ]}
                    actions={
                      <>
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          className="flex-1"
                          onClick={handleTestConnection}
                          disabled={validateTokenMutation.isPending}
                          data-testid="intercom-test-connection-button"
                        >
                          {validateTokenMutation.isPending ? (
                            <Spinner className="size-4" />
                          ) : (
                            <CheckCircle2 className="size-4" />
                          )}
                          {t(`${INTERCOM_NS_PREFIX}.test_connection`)}
                        </Button>
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          className="flex-1"
                          onClick={handleAuthenticate}
                          data-testid="intercom-reauthenticate-button"
                        >
                          <RefreshCw className="size-4" />
                          {t('integrations:reconnect')}
                        </Button>
                        <Button
                          type="button"
                          variant="danger"
                          size="sm"
                          className="flex-1"
                          onClick={() => setConfirmRemoveOpen(true)}
                          data-testid="intercom-remove-button"
                        >
                          <Unplug className="size-4" />
                          {t('integrations:disconnect')}
                        </Button>
                      </>
                    }
                  />
                )}
              </IntegrationSection>

              <IntegrationSection
                title={t('integrations:section.settings')}
                data-testid="intercom-settings-section"
              >
                <div className="space-y-4" data-testid="intercom-form">
                  <IntegrationToggleField
                    id="intercom-active-toggle"
                    label={t('integrations:enable', { name: 'Intercom' })}
                    checked={Boolean(integrationData?.intercom_bot_active)}
                    onCheckedChange={handleToggleActive}
                    disabled={!isConnected || toggleActiveMutation.isPending}
                    data-testid="intercom-active-toggle"
                  />

                  <Controller
                    control={control}
                    name="reply_to_all"
                    render={({ field }) => (
                      <IntegrationToggleField
                        id="intercom-reply-to-all-toggle"
                        label={t(`${INTERCOM_NS_PREFIX}.reply_to_all.label`)}
                        tooltip={t(`${INTERCOM_NS_PREFIX}.reply_to_all.description`)}
                        checked={field.value}
                        onCheckedChange={(checked) => {
                          field.onChange(checked);
                          patchIntegrationMutation.mutate({
                            name: INTERCOM_INTEGRATION_NAME,
                            params: { intercom_only_admin: !checked },
                          });
                        }}
                        disabled={!isConnected}
                        data-testid="intercom-reply-to-all-toggle"
                      />
                    )}
                  />

                  <Controller
                    control={control}
                    name="intercom_handoff"
                    render={({ field }) => (
                      <IntegrationToggleField
                        id="intercom-handoff-toggle"
                        label={t(`${INTERCOM_NS_PREFIX}.handoff.label`)}
                        tooltip={t(`${INTERCOM_NS_PREFIX}.handoff.description`)}
                        checked={field.value}
                        onCheckedChange={(checked) => {
                          field.onChange(checked);
                          patchIntegrationMutation.mutate({
                            name: INTERCOM_INTEGRATION_NAME,
                            params: { intercom_handoff: checked },
                          });
                        }}
                        disabled={!isConnected}
                        data-testid="intercom-handoff-toggle"
                      />
                    )}
                  />

                  <Controller
                    control={control}
                    name="auto_close"
                    render={({ field }) => (
                      <IntegrationControlField
                        id="intercom-auto-close-select"
                        label={t(`${INTERCOM_NS_PREFIX}.auto_close.label`)}
                      >
                        <Select
                          value={field.value}
                          onValueChange={(value) => {
                            field.onChange(value);
                            patchAutoCloseMutation.mutate(parseAutoCloseKey(value));
                          }}
                          disabled={!isConnected}
                        >
                          <SelectTrigger
                            id="intercom-auto-close-select"
                            className="w-full"
                            data-testid="intercom-auto-close-select"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem
                              value={AUTO_CLOSE_NEVER_SENTINEL}
                              data-testid="intercom-auto-close-option-never"
                            >
                              {t(`${INTERCOM_NS_PREFIX}.auto_close.options.never`)}
                            </SelectItem>
                            <SelectItem value="5" data-testid="intercom-auto-close-option-5">
                              {t(`${INTERCOM_NS_PREFIX}.auto_close.options.5`)}
                            </SelectItem>
                            <SelectItem value="10" data-testid="intercom-auto-close-option-10">
                              {t(`${INTERCOM_NS_PREFIX}.auto_close.options.10`)}
                            </SelectItem>
                            <SelectItem value="30" data-testid="intercom-auto-close-option-30">
                              {t(`${INTERCOM_NS_PREFIX}.auto_close.options.30`)}
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </IntegrationControlField>
                    )}
                  />
                </div>
              </IntegrationSection>
            </>
          )}
        </div>

        <AlertDialogPrimitive.Root open={confirmRemoveOpen} onOpenChange={setConfirmRemoveOpen}>
          <AlertDialogPrimitive.Overlay className="!mt-0 absolute inset-0 bg-black/30 backdrop-blur-sm z-20" />
          <AlertDialogPrimitive.Content className="absolute inset-0 flex items-center justify-center p-4 z-30 focus:outline-none">
            <div
              className="space-y-4 bg-card rounded-lg p-6 max-w-md w-full shadow-lg"
              data-testid="intercom-remove-dialog"
            >
              <div className="flex items-center gap-3">
                <AlertTriangle className="size-6 text-destructive" />
                <h2 className="text-lg font-semibold text-foreground">
                  {t(`${INTERCOM_NS_PREFIX}.remove_confirm.title`)}
                </h2>
              </div>
              <p className="text-sm text-muted-foreground">
                {t(`${INTERCOM_NS_PREFIX}.remove_confirm.description`)}
              </p>
              <div className="flex justify-end gap-2">
                <AlertDialogPrimitive.Cancel asChild>
                  <Button variant="secondary" size="md" data-testid="intercom-remove-cancel-button">
                    {t(`${INTERCOM_NS_PREFIX}.remove_confirm.cancel`)}
                  </Button>
                </AlertDialogPrimitive.Cancel>
                <Button
                  variant="danger"
                  size="md"
                  onClick={handleRemove}
                  disabled={removeMutation.isPending}
                  data-testid="intercom-remove-confirm-button"
                >
                  {removeMutation.isPending ? (
                    <Spinner className="size-4" />
                  ) : (
                    <XCircle className="size-4" />
                  )}
                  {t(`${INTERCOM_NS_PREFIX}.remove_confirm.confirm`)}
                </Button>
              </div>
            </div>
          </AlertDialogPrimitive.Content>
        </AlertDialogPrimitive.Root>
      </SheetContent>
    </Sheet>
  );
};
